import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useEvolutionSolde30j } from '@/hooks/queries/useCaisseGroupe';
import { DataCard, SkeletonShimmer } from '@/components/design-system';
import { formatDA, formatDACompact } from '@/lib/format';

export function EvolutionSoldeChart(): JSX.Element {
  const { data: serie = [], isLoading } = useEvolutionSolde30j();

  const minSolde = useMemo(() => Math.min(...serie.map((s) => s.solde), 0), [serie]);
  const annotations = useMemo(
    () => serie.filter((s) => s.evenement && Math.abs(s.variation) > 0).slice(0, 5),
    [serie],
  );

  return (
    <DataCard
      title="Évolution solde 30j"
      subtitle="Projection journalière (échéances + charges récurrentes)"
    >
      {isLoading ? (
        <SkeletonShimmer variant="hero" />
      ) : serie.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-500">Aucune donnée à projeter.</div>
      ) : (
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={serie}>
              <defs>
                <linearGradient id="solde-grad-pos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="solde-grad-neg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#f43f5e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="jour" tick={{ fontSize: 10 }} interval={2} />
              <YAxis tickFormatter={(v: number) => formatDACompact(v)} tick={{ fontSize: 10 }} />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p = payload[0]?.payload as
                    | { jour: string; solde: number; variation: number; evenement?: string }
                    | undefined;
                  if (!p) return null;
                  return (
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
                      <div className="font-bold text-slate-800">{p.jour}</div>
                      <div className="mt-1 text-slate-700">Solde : {formatDA(p.solde)}</div>
                      {p.variation !== 0 && (
                        <div className={p.variation < 0 ? 'text-rose-600' : 'text-emerald-600'}>
                          Variation : {p.variation > 0 ? '+' : ''}
                          {formatDA(p.variation)}
                        </div>
                      )}
                      {p.evenement && (
                        <div className="mt-1 max-w-[200px] text-[10px] text-slate-500">
                          {p.evenement}
                        </div>
                      )}
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="solde"
                stroke={minSolde < 0 ? '#f43f5e' : '#10b981'}
                fill={minSolde < 0 ? 'url(#solde-grad-neg)' : 'url(#solde-grad-pos)'}
                strokeWidth={2}
              />
              {annotations.map((a) => (
                <ReferenceDot
                  key={a.date}
                  x={a.jour}
                  y={a.solde}
                  r={4}
                  fill="#f59e0b"
                  stroke="#fff"
                  strokeWidth={1.5}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </DataCard>
  );
}
